// src/components/ui/Button.jsx
import React from "react";

export function MagicIcon({ className = "w-5 h-5" }) {
    return (
        <svg
            viewBox="0 0 24 24"
            className={className}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
        >
            <path
                d="M6 18L18 6M8 6l2 2M14 12l2 2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export default function Button({
    children,
    type = "button",
    variant = "primary", // "primary" | "soft" | "magic" | "danger" | "neutral"
    size = "md", // "sm" | "md" | "lg"
    full = false,
    disabled = false,
    loading = false,
    startIcon,
    endIcon,
    iconOnly = false,
    className = "",
    ...props
}) {
    // 👇 más compacto en mobile, mismo “peso” que antes en sm+
    const textSizes = {
        sm: "text-xs px-2.5 py-1.5 rounded sm:text-sm sm:px-3 sm:py-2",
        md: "text-sm px-3 py-2.5 rounded sm:text-base sm:px-4 sm:py-3",
        lg: "text-sm px-3.5 py-2.5 rounded sm:text-base sm:px-5 sm:py-3.5",
    };

    const iconSquare = {
        sm: "h-8 w-8 rounded sm:h-9 sm:w-9",
        md: "h-9 w-9 rounded sm:h-10 sm:w-10",
        lg: "h-10 w-10 rounded sm:h-11 sm:w-11",
    };

    const iconSizes = { sm: "w-4 h-4", md: "w-5 h-5", lg: "w-5 h-5" };

    const variants = {
        primary: [
            "bg-primary text-white font-medium",
            "hover:opacity-90 transition-opacity",
        ].join(" "),

        soft: [
            "bg-primary/10 dark:bg-primary/20 text-primary font-medium",
            "hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors",
        ].join(" "),

        magic: [
            "group relative overflow-hidden text-white font-bold",
            "gradient-bio animate-bio-pulse",
            "transition-all duration-500 ease-in-out",
        ].join(" "),

        danger: [
            "bg-danger-light/10 dark:bg-danger-dark/20 text-danger-light dark:text-danger-dark font-medium",
            "hover:bg-danger-light/20 dark:hover:bg-danger-dark/30 transition-colors",
        ].join(" "),

        // botón discreto y neutro
        neutral: [
            "bg-gray-50 dark:bg-white/5 text-foreground-light dark:text-foreground-dark font-medium",
            "hover:bg-gray-100 dark:hover:bg-white/10 transition-colors",
        ].join(" "),

        outline: [
            "bg-transparent border-2 border-border-light dark:border-border-dark text-foreground-light dark:text-foreground-dark font-medium",
            "hover:bg-gray-50 dark:hover:bg-white/5 transition-colors",
        ].join(" "),
    };

    const base =
        "inline-flex items-center justify-center select-none shadow-none " +
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary " +
        "focus-visible:ring-offset-2 focus-visible:ring-offset-background-light " +
        "dark:focus-visible:ring-offset-background-dark " +
        "disabled:opacity-60 disabled:cursor-not-allowed";

    const withTextLayout = [
        full ? "w-full" : "",
        textSizes[size] ?? textSizes.md,
        "gap-2",
    ].join(" ");

    const iconOnlyLayout = [iconSquare[size] ?? iconSquare.md].join(" ");

    const effectiveStartIcon = loading ? (
        <svg
            className="animate-spin w-full h-full text-current"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
        </svg>
    ) : startIcon;

    const content = iconOnly ? (
        <span className={`relative z-10 inline-flex ${iconSizes[size] ?? iconSizes.md}`}>
            {effectiveStartIcon || endIcon}
        </span>
    ) : (
        <>
            {effectiveStartIcon && (
                <span className={`relative z-10 inline-flex shrink-0 ${iconSizes[size]}`}>
                    {effectiveStartIcon}
                </span>
            )}
            <span className="relative z-10 truncate">{children}</span>
            {endIcon && (
                <span className={`relative z-10 inline-flex shrink-0 ${iconSizes[size]}`}>
                    {endIcon}
                </span>
            )}
        </>
    );

    return (
        <button
            type={type}
            disabled={disabled || loading}
            className={[
                base,
                variants[variant] ?? variants.primary,
                iconOnly ? iconOnlyLayout : withTextLayout,
                className,
            ].join(" ")}
            {...props}
        >
            {variant === "magic" && (
                <>
                    <span className="pointer-events-none absolute inset-0 gradient-bio opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                    <span className="pointer-events-none absolute inset-0 transform-gpu transition-transform duration-500 group-hover:scale-110">
                        <span className="absolute -left-1/2 -top-1/2 h-[200%] w-[200%] bg-gradient-to-br from-white/30 via-white/0 to-white/30 opacity-0 transition-opacity duration-700 group-hover:opacity-100 animate-[spin_10s_linear_infinite]" />
                    </span>
                </>
            )}
            {content}
        </button>
    );
}